/* eslint-disable @typescript-eslint/no-magic-numbers -- Hanyagság. */

import connectedCallback from '@common/lifecycles/connectedCallback'

import { is } from '@common/is/is'
import { Number } from '@common/Number/Number'

import DOM from '@common/DOM/DOM'

import styles from './MemoryStats.scss'

const PERFORMANCE = window.performance

const MS_GRAPH_BAR_LIMIT = 74

const BYTE_SIZE = 1024

const SECOND_IN_MS = 1000
const FPS_LIMIT = 30

/** MemoryStats. */
export default function MemoryStats (): HTMLElement | null {
  let msMin = 100
  let msMax = 0

  const GRAPH_HEIGHT = 30

  let redrawMbThreshold = GRAPH_HEIGHT

  const containerEl: HTMLElement = (
    <div className={ styles.container } />
  )

  const msDivEl: HTMLElement = (
    <div
      className={ styles.ms }
    />
  )

  containerEl.appendChild(msDivEl)

  const msTextEl: HTMLElement = (
    <div className={ styles.msText }>
      Memory
    </div>
  )

  msDivEl.appendChild(msTextEl)

  const msGraphEl: HTMLModElement = (
    <div
      className={ styles.msGraph }
      style={ { height: `${ GRAPH_HEIGHT }px` } }
    />
  )

  msDivEl.appendChild(msGraphEl)

  while (msGraphEl.querySelectorAll(`.${ styles.msGraphBar }`).length < MS_GRAPH_BAR_LIMIT) {
    const bar: HTMLElement = (
      <span
        className={ styles.msGraphBar }
        style={ { height: `${ GRAPH_HEIGHT }px` } }
      />
    )

    msGraphEl.appendChild(bar)
  }

  /**
   * Graph frissítése.
   * @param height - Magasság.
   * @param color  - Szín.
   */
  function updateGraph (height: number, color: string): void {
    const firstBar = msGraphEl.querySelector(`.${ styles.msGraphBar }`)

    if (!is.instanceOf(firstBar, HTMLElement)) {
      return
    }

    firstBar.style.height = `${ height  }px`

    if (color) {
      firstBar.style.backgroundColor = color
    }

    msGraphEl.appendChild(firstBar)
  }

  /**
   * Graph újrarajzolása.
   * @param oHfactor - Régi faktor.
   * @param hFactor  - Új faktor.
   */
  function redrawGraph (oHfactor: number, hFactor: number): void {
    const bars: HTMLElement[] = Array.from(msGraphEl.querySelectorAll(`.${ styles.msGraphBar }`))

    bars.forEach((c) => {
      const cHeight = Number.parseInt(c.style.height.slice(0, Math.max(0, c.style.height.length - 2)))

      // Convert to MB, change factor
      const newVal = GRAPH_HEIGHT - (((GRAPH_HEIGHT - cHeight) / oHfactor) * hFactor)

      c.style.height = `${ newVal }px`
    })
  }

  const sizes = [ 'Bytes', 'KB', 'MB', 'GB', 'TB' ] as const

  let precision: number
  let i: number

  /**
   * Byte méret konvertálása.
   * @param bytes       - Byte méret.
   * @param nFractDigit - Tizedesjegyek száma.
   */
  function bytesToSize (bytes: number, nFractDigit: number | undefined): string {
    if (bytes === 0) {
      return 'n/a'
    }

    const validnFractDigit = is.undefined(nFractDigit)
      ? 0
      : nFractDigit

    precision = 10 ** validnFractDigit

    i = Math.floor(Math.log(bytes) / Math.log(BYTE_SIZE))

    return `${ Math.round(bytes * precision / (BYTE_SIZE ** i)) / precision } ${ sizes[i] }`
  }

  if (!is.property(PERFORMANCE, 'memory')) {
    return null
  }

  if (!is.property(PERFORMANCE.memory, 'usedJSHeapSize')) {
    return null
  }

  if (!is.number(PERFORMANCE.memory.usedJSHeapSize)) {
    return null
  }

  let lastTime = Date.now()
  let lastUsedHeap = PERFORMANCE.memory.usedJSHeapSize

  let delta = 0
  let color = '#131'
  let ms = 0
  let mbValue = 0
  let factor = 0
  let newThreshold = 0

  /** Frissítés. */
  function refresh (): void {
    // update at 30fps
    if (Date.now() - lastTime < (SECOND_IN_MS / FPS_LIMIT)) {
      return
    }

    if (!is.property(PERFORMANCE, 'memory')) {
      return
    }

    if (!is.property(PERFORMANCE.memory, 'usedJSHeapSize')) {
      return
    }

    if (!is.number(PERFORMANCE.memory.usedJSHeapSize)) {
      return
    }

    lastTime = Date.now()

    delta = PERFORMANCE.memory.usedJSHeapSize - lastUsedHeap
    lastUsedHeap = PERFORMANCE.memory.usedJSHeapSize

    // if memory has gone down, consider it a GC and draw a red bar.
    color = delta < 0 ? '#830' : '#131'

    ms = lastUsedHeap
    msMin = Math.min(msMin, ms)
    msMax = Math.max(msMax, ms)

    msTextEl.textContent = `Mem: ${ bytesToSize(ms, 2) }`

    mbValue = ms / (BYTE_SIZE * BYTE_SIZE)

    if (mbValue > redrawMbThreshold) {
      factor = (mbValue - (mbValue % GRAPH_HEIGHT)) / GRAPH_HEIGHT
      newThreshold = GRAPH_HEIGHT * (factor + 1)

      redrawGraph(GRAPH_HEIGHT / redrawMbThreshold, GRAPH_HEIGHT / newThreshold)

      redrawMbThreshold = newThreshold
    }

    const newHeight = GRAPH_HEIGHT - (mbValue * (GRAPH_HEIGHT / redrawMbThreshold))

    updateGraph(newHeight, color)
  }

  /** Frissítés. */
  function update (): void {
    refresh()

    if (DOM.isAttachedToDOM(containerEl)) {
      requestAnimationFrame(update)
    }
  }

  connectedCallback(containerEl, () => {
    requestAnimationFrame(update)
  })

  return containerEl
}
